import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import {
  FaRupeeSign,
  FaUserGraduate,
  FaShoppingCart,
  FaTrophy,
  FaSyncAlt,
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Bar, Line } from "react-chartjs-2";
import { getAdminDashboard } from "../../services/authService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [year, setYear] = useState(currentYear);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboard = async (filters = {}) => {
    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate !== undefined ? filters.startDate : startDate,
        endDate: filters.endDate !== undefined ? filters.endDate : endDate,
        year: filters.year !== undefined ? filters.year : year,
        period: filters.period !== undefined ? filters.period : period,
      };
      const res = await getAdminDashboard(params);
      if (res && res.data) {
        setDashboardData(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch initial data once on mount
  useEffect(() => {
    fetchDashboard({ startDate: "", endDate: "", year: currentYear, period: "monthly" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchDashboard({ startDate, endDate, year, period });
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setYear(currentYear);
    setPeriod("monthly");
    fetchDashboard({ startDate: "", endDate: "", year: currentYear, period: "monthly" });
  };

  // 📊 Revenue Trend (Line Chart)
  const revenueGrowth = dashboardData?.revenueGrowth;
  const revenueLabels = revenueGrowth?.labels || ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const revenueSeries = revenueGrowth?.series?.[0]?.data || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

  const revenueChartData = {
    labels: revenueLabels,
    datasets: [
      {
        label: revenueGrowth?.series?.[0]?.name || "Monthly Revenue (₹)",
        data: revenueSeries,
        borderColor: "#C9A227",
        backgroundColor: "rgba(201, 162, 39, 0.2)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#1A3C8B",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  };

  // 📈 Enrollments vs Completed (Bar Chart)
  const enrollmentsVsCompletions = dashboardData?.enrollmentsVsCompletions;
  const evcLabels = enrollmentsVsCompletions?.labels || revenueLabels;
  const newEnrollmentsData = enrollmentsVsCompletions?.series?.[0]?.data || [];
  const completedCoursesData = enrollmentsVsCompletions?.series?.[1]?.data || [];

  const enrollmentsChartData = {
    labels: evcLabels,
    datasets: [
      {
        label: enrollmentsVsCompletions?.series?.[0]?.name || "New Enrollments",
        data: newEnrollmentsData,
        backgroundColor: enrollmentsVsCompletions?.series?.[0]?.color || "#1A3C8B",
        borderRadius: 6,
      },
      {
        label: enrollmentsVsCompletions?.series?.[1]?.name || "Completed Courses",
        data: completedCoursesData,
        backgroundColor: enrollmentsVsCompletions?.series?.[1]?.color || "#6C1E1E",
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          color: "#333",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              label += context.dataset.label?.includes("Revenue")
                ? "₹ " + Number(context.parsed.y).toLocaleString("en-IN")
                : context.parsed.y;
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const overview = dashboardData?.overview;
  const topCourse = dashboardData?.topPerformingCourse;

  return (
    <div className="law-dashboard-wrapper">
      <div className="dashboard-header-section">
        <div>
          <h2 className="dashboard-title">Business Overview</h2>
          <p className="dashboard-subtitle">
            Monitor your academy's growth, revenue, and student engagement in real-time.
          </p>
        </div>
        <button
          className="btn-export d-flex align-items-center gap-2"
          onClick={() => fetchDashboard()}
          disabled={loading}
        >
          <FaSyncAlt className={loading ? "fa-spin" : ""} /> Refresh
        </button>
      </div>

      {/* 📅 Date & Year Filter Section */}
      <div
        style={{
          padding: "16px",
          background: "#fff",
          margin: "0 0 24px 0",
          borderRadius: "12px",
          boxShadow: "0 5px 20px rgba(0,0,0,0.03)",
        }}
      >
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Start Date
            </label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              End Date
            </label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label text-muted" style={{ fontSize: "13px", fontWeight: "bold" }}>
              Year
            </label>
            <select
              className="form-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 d-flex justify-content-end gap-2">
            <button className="btn btn-secondary px-4" onClick={handleClear} disabled={loading}>
              Clear
            </button>
            <button
              className="btn text-white px-4"
              onClick={handleSearch}
              disabled={loading}
              style={{ background: "#1A3C8B", borderColor: "#1A3C8B" }}
            >
              {loading ? "Loading..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* 🚀 Key Performance Indicators (KPIs) */}
      <div className="kpi-grid">
        <div className="kpi-card glass-blue">
          <div className="kpi-icon-wrapper">
            <FaRupeeSign />
          </div>
          <div className="kpi-details">
            <p className="kpi-label">Total Revenue</p>
            <h3 className="kpi-value">
              {overview?.totalRevenueFormatted ||
                (overview?.totalRevenue !== undefined
                  ? `₹ ${Number(overview.totalRevenue).toLocaleString("en-IN")}`
                  : "₹ 0")}
            </h3>
          </div>
        </div>

        <div className="kpi-card glass-gold">
          <div className="kpi-icon-wrapper">
            <FaUserGraduate />
          </div>
          <div className="kpi-details">
            <p className="kpi-label">Active Students</p>
            <h3 className="kpi-value">
              {overview?.activeStudentsFormatted ||
                (overview?.activeStudents !== undefined
                  ? Number(overview.activeStudents).toLocaleString("en-IN")
                  : "0")}
            </h3>
          </div>
        </div>

        <div className="kpi-card glass-red">
          <div className="kpi-icon-wrapper">
            <FaShoppingCart />
          </div>
          <div className="kpi-details">
            <p className="kpi-label">Total Subscriptions</p>
            <h3 className="kpi-value">
              {overview?.totalSubscriptionsFormatted ||
                (overview?.totalSubscriptions !== undefined
                  ? Number(overview.totalSubscriptions).toLocaleString("en-IN")
                  : "0")}
            </h3>
          </div>
        </div>
      </div>

      {/* 📊 Analytics Section */}
      <div className="analytics-grid-single">
        <div className="analytics-card">
          <div className="card-header">
            <h4>Revenue Growth</h4>
            <span className="badge-monthly">{period === "monthly" ? `Monthly (${year})` : `Yearly`}</span>
          </div>
          <div className="chart-container">
            <Line data={revenueChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* 📈 Secondary Analytics */}
      <div className="analytics-grid mt-4">
        <div className="analytics-card col-span-2">
          <div className="card-header">
            <h4>Enrollments vs Completions</h4>
          </div>
          <div className="chart-container">
            <Bar data={enrollmentsChartData} options={chartOptions} />
          </div>
        </div>

        <div className="analytics-card bg-highlight">
          <div className="card-header">
            <h4>Top Performing Course</h4>
          </div>
          <div className="top-course-content">
            <div className="trophy-icon">
              <FaTrophy />
            </div>
            <h3>{topCourse?.courseTitle || "Judiciary Target Course"}</h3>
            <p className="course-stats">
              {topCourse?.enrolledFormatted || `${topCourse?.enrolledCount || 0} Enrolled`} •{" "}
              {topCourse?.revenueFormatted || `₹ ${(topCourse?.revenue || 0).toLocaleString("en-IN")} Revenue`}
            </p>
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${topCourse?.conversionRate || 0}%` }}
              ></div>
            </div>
            <p className="conversion-text">
              {topCourse?.conversionRateFormatted || `${topCourse?.conversionRate || 0}% Conversion Rate`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}